import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ErrorState, PageHeader, Skeleton } from '@/components/ui';
import { FavoriteButton, LearnedButton } from '@/components/SignActions';
import { DownloadButton } from '@/components/DownloadButton';
import { MediaStatusBadge } from '@/components/MediaStatusBadge';
import { VideoPlayer } from '@/components/VideoPlayer';
import { recordView } from '@/db/repositories/history';
import type { MediaAvailability } from '@/services/media/types';
import { useMedia } from '@/features/media/MediaProvider';
import { updateState } from '@/db/repositories/localSignState';
import {
  useCategory,
  useSign,
  useSignNeighbours,
  useSignState,
} from '@/hooks/useCatalogQueries';

export function SignPage(): React.ReactElement {
  const { signId } = useParams<{ signId: string }>();

  const sign = useSign(signId);
  const state = useSignState(signId);
  const category = useCategory(sign?.categoryId);
  const neighbours = useSignNeighbours(sign);

  const [availability, setAvailability] = useState<MediaAvailability | null>(null);

  const { resolver } = useMedia();

  // Registra a visualização (F11-T09). Depende só do id: reabrir o mesmo sinal
  // depois de favoritar não deveria gerar uma nova entrada de histórico.
  useEffect(() => {
    if (!signId || !sign) return;
    void recordView(signId);
  }, [signId, sign?.id]);

  useEffect(() => {
    if (!sign) return;
    let cancelled = false;

    void resolver.availability(sign).then((result) => {
      if (!cancelled) setAvailability(result);
    });

    return () => {
      cancelled = true;
    };
  }, [sign, resolver, state?.offline.status]);

  if (sign === undefined) {
    return (
      <>
        <PageHeader title="Sinal" backTo="/" />
        <Skeleton className="aspect-video w-full" />
      </>
    );
  }

  if (sign === null) {
    return (
      <>
        <PageHeader title="Sinal" backTo="/" />
        <ErrorState
          title="Sinal não encontrado"
          description="Ele pode ter saído do glossário na última atualização."
        />
      </>
    );
  }

  const backTo = category ? `/category/${encodeURIComponent(category.id)}` : '/';

  return (
    <>
      <PageHeader
        title={sign.name}
        backTo={backTo}
        subtitle={category?.name}
        actions={availability ? <MediaStatusBadge availability={availability} compact /> : undefined}
      />

      <VideoPlayer
        sign={sign}
        resolver={resolver}
        // Um arquivo que não decodifica não pode continuar marcado como disponível:
        // é justamente essa mentira que só apareceria no modo avião.
        onLocalFileBroken={(brokenId) => {
          void updateState(brokenId, (current) => ({
            ...current,
            offline: {
              ...current.offline,
              status: 'error',
              error: 'O arquivo salvo não pôde ser reproduzido.',
            },
          }));
        }}
      />

      {sign.reference && (
        <p className="mt-3 rounded-xl bg-slate-800 p-3 text-sm text-slate-300">{sign.reference}</p>
      )}

      {state?.offline.outdated && (
        <p role="status" className="mt-3 rounded-xl bg-amber-500/15 p-3 text-sm text-amber-200">
          O vídeo deste sinal mudou na fonte. O arquivo salvo ainda funciona, mas está
          desatualizado.
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <FavoriteButton signId={sign.id} active={state?.favorite ?? false} />
        <LearnedButton signId={sign.id} active={state?.learned ?? false} />
      </div>

      <div className="mt-2 flex gap-2">
        <DownloadButton sign={sign} state={state} />
      </div>

      {availability && (
        <div className="mt-3">
          <MediaStatusBadge availability={availability} />
        </div>
      )}

      {/* Navegação anterior/próximo dentro da categoria (F8-T06). */}
      <nav aria-label="Navegar entre sinais" className="mt-6 flex gap-2 pb-4">
        {neighbours?.previous ? (
          <Link
            to={`/sign/${encodeURIComponent(neighbours.previous.id)}`}
            className="flex min-h-12 flex-1 items-center gap-1 rounded-xl bg-slate-800 px-3 text-sm active:bg-slate-700"
          >
            <ChevronLeft size={18} aria-hidden />
            <span className="truncate">{neighbours.previous.name}</span>
          </Link>
        ) : (
          <span className="flex-1" />
        )}

        {neighbours?.next ? (
          <Link
            to={`/sign/${encodeURIComponent(neighbours.next.id)}`}
            className="flex min-h-12 flex-1 items-center justify-end gap-1 rounded-xl bg-slate-800 px-3 text-sm active:bg-slate-700"
          >
            <span className="truncate">{neighbours.next.name}</span>
            <ChevronRight size={18} aria-hidden />
          </Link>
        ) : (
          <span className="flex-1" />
        )}
      </nav>
    </>
  );
}
